import React, { useEffect, useState, useMemo } from 'react';
import styled from '@emotion/styled-base';
import { captureException, withScope } from '@sentry/browser';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';

import computerDeskIllustrationUrl from 'src/public/ds/illustrations/marginalia-girl-computer.svg';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import {
    BodyTextMedium,
    SelectField,
    Option,
    Button,
    pxToRem,
} from '@modernhealth/vital-components';
import { useGetCompanyInsuranceCarriersQuery } from '../documents/GetCompanyInsuranceCarriers.generated';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { getMyTherapists } from 'src/modules/providers/selectors/providersSelectors';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { useHpiEligibilityFlow } from '../hooks/useHpiEligibilityFlow';
import { HpiEligibilityFlow } from 'src/shared/models/shared';
import { useHpiEligibilityFlowAnalytics } from '../analytics';
import { FlowLayout } from '../components/FlowLayout';

interface IllustrationProps {
    illustrationUrl: string;
}

export enum CheckForInsurancePageTestID {
    Select = 'CheckForInsurancePageTestID.Select',
}

const NO_INSURANCE = 'NO_INSURANCE';

const selectStyles = {
    container: () => ({
        marginBottom: pxToRem(20),
    }),
};

const PageContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 'calc(500px + 2 * 1rem)', // 500px + 2x padding
    padding: '0 1rem',
    margin: '0 auto 3rem',
});

const ComputerDeskIllustration = styled('div')<IllustrationProps>(({ illustrationUrl }) => ({
    display: 'inline-block',
    width: 280,
    height: 280,
    backgroundImage: `url('${illustrationUrl}')`,
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
}));

type Props = RouteComponentProps<
    {},
    {},
    {
        insuranceId?: string;
        routedFromProfilePage?: boolean;
        hpiEligibilityFlow?: HpiEligibilityFlow;
    } & Record<string, unknown>
>;

const analyticsTrackPageName = 'Health Plan selection';

export const CheckForInsurancePage: React.FC<Props> = ({ history, location }) => {
    const { t } = useTranslation();
    const {
        company,
        companyUuid,
        hpiEligibilityStatus,
        hpiMemberInsurance,
        isCompanyHpiEnabled,
        isCompanyPsychiatryEnabled,
    } = useSelector(getUserInformation);
    const myTherapists = useSelector(getMyTherapists);
    const hpiEligibilityFlow = useHpiEligibilityFlow();
    const analytics = useHpiEligibilityFlowAnalytics(
        'inquire-employer-insurance-coverage',
        hpiEligibilityFlow,
    );

    const [selectedInsuranceId, setSelectedInsuranceId] = useState<string | null>(
        location?.state?.insuranceId || null,
    );

    const routedFromProfilePage = location?.state?.routedFromProfilePage ?? false;

    const session = useMemo(() => {
        if (location.search) {
            const searchParams = new URLSearchParams(location.search);

            if (searchParams.get('session')) {
                return searchParams.get('session');
            }
        }

        return location?.state?.session;
    }, [location]);

    const goToInsuranceApproved = () => {
        history.push({
            pathname: '/insurance-approved',
            state: {
                hpiEligibilityFlow: hpiEligibilityFlow,
            },
        });
    };

    useEffect(() => {
        if (routedFromProfilePage) return;

        if (hpiEligibilityStatus && hpiMemberInsurance) {
            if (hpiEligibilityFlow === HpiEligibilityFlow.Psychiatry) {
                goToInsuranceApproved();
            } else if (session) {
                history.push({
                    pathname: '/payments/session-review',
                    state: {
                        session,
                        onboardingMode: false,
                    },
                });
            } else {
                switch (myTherapists.length) {
                    case 1:
                        history.push({
                            pathname: '/schedule-session/calendar',
                            state: {
                                coachUuid: myTherapists[0].uuid,
                                isFirstSession: false,
                                disableBack: true,
                            },
                        });
                        break;
                    case 0:
                        history.push('/dashboard');
                        break;
                    default:
                        history.push('/schedule-session/select-coach');
                        break;
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { data, loading: isGettingInsuranceCarriers } = useGetCompanyInsuranceCarriersQuery({
        variables: { employerUuid: companyUuid },
        onError: captureException,
        onCompleted: responseData => {
            if (!responseData?.getEmployerInsurances) {
                toastNotificationsService.renderErrorToast(config.messages.genericError);

                withScope(scope => {
                    scope.setExtra('response', responseData);
                    const errMsg =
                        'Did not receive correct payload in the response in useGetCompanyInsuranceCarriersQuery';
                    captureException(new Error(errMsg));
                });
            }
        },
    });

    const insuranceOptions = useMemo(() => {
        return (data?.getEmployerInsurances || []).reduce((acc, curr) => {
            return curr?.employerInsuranceId && curr?.name
                ? [
                      ...acc,
                      {
                          value: curr.employerInsuranceId.toString(),
                          label: curr.name,
                      },
                  ]
                : acc;
        }, [] as Option[]);
    }, [data]);

    const selectOptions = useMemo(
        () => [
            ...insuranceOptions,
            {
                value: NO_INSURANCE,
                label: t('insuranceForm:insurance_inquiry.no_health_plan'),
            },
        ],
        [insuranceOptions, t],
    );

    const doesHaveOnlyOneInsuranceOption = insuranceOptions.length === 1;

    const handleContinueClick = () => {
        if (!selectedInsuranceId) {
            return;
        }

        analytics.trackClick('Inquire Insurance Coverage: Yes Coverage', {
            page_name: analyticsTrackPageName,
        });

        history.replace({
            state: {
                insuranceId: selectedInsuranceId,
                session,
                routedFromProfilePage,
                hpiEligibilityFlow,
            },
        });

        if (selectedInsuranceId !== NO_INSURANCE) {
            const selectedInsurance = (data?.getEmployerInsurances || []).find(
                insurance => insurance?.employerInsuranceId?.toString() === selectedInsuranceId,
            );

            history.push('/enter-insurance-plan-information', {
                employerInsurance: selectedInsurance,
                session,
                routedFromProfilePage,
                hpiEligibilityFlow,
            });
        } else {
            history.push('/insurance-ineligible', { routedFromProfilePage, hpiEligibilityFlow });
        }
    };

    useEffect(() => {
        if (doesHaveOnlyOneInsuranceOption) {
            setSelectedInsuranceId(insuranceOptions[0].value);
        }
    }, [insuranceOptions, doesHaveOnlyOneInsuranceOption]);

    const redirectForTherapy =
        hpiEligibilityFlow === HpiEligibilityFlow.Therapy && !isCompanyHpiEnabled;
    const redirectForPsychiatry =
        hpiEligibilityFlow === HpiEligibilityFlow.Psychiatry && !isCompanyPsychiatryEnabled;
    if (redirectForTherapy || redirectForPsychiatry) {
        return <Redirect to="/dashboard" />;
    }

    if (isGettingInsuranceCarriers) {
        return (
            <PageContainer>
                <CenteredSpinner />
            </PageContainer>
        );
    }

    return (
        <FlowLayout
            illustration={
                <ComputerDeskIllustration illustrationUrl={computerDeskIllustrationUrl} />
            }
            heading={
                doesHaveOnlyOneInsuranceOption
                    ? t('insuranceForm:insurance_inquiry.do_you_have_exact_insurance_title', {
                          insuranceName: insuranceOptions[0].label,
                          companyName: company,
                      })
                    : t('insuranceForm:insurance_inquiry.do_you_have_insurance_title', {
                          companyName: company,
                      })
            }
            actions={[
                <Button
                    key={0}
                    style={{ flex: 1 }}
                    variant="secondary"
                    size="large"
                    onClick={() => {
                        analytics.trackClick('Inquire Insurance Coverage: No Coverage', {
                            page_name: analyticsTrackPageName,
                        });
                        history.push('/insurance-ineligible', {
                            routedFromProfilePage,
                            hpiEligibilityFlow,
                        });
                    }}
                >
                    {doesHaveOnlyOneInsuranceOption
                        ? t('insuranceForm:insurance_inquiry.buttons.no')
                        : t('insuranceForm:insurance_inquiry.buttons.complete_later')}
                </Button>,
                <Button
                    key={1}
                    style={{ flex: 1 }}
                    disabled={!doesHaveOnlyOneInsuranceOption && !selectedInsuranceId}
                    variant="primary"
                    size="large"
                    onClick={handleContinueClick}
                >
                    {doesHaveOnlyOneInsuranceOption
                        ? t('insuranceForm:insurance_inquiry.buttons.yes')
                        : t('common.continue')}
                </Button>,
            ]}
        >
            <BodyTextMedium marginBottom={24}>
                {hpiEligibilityFlow !== HpiEligibilityFlow.Psychiatry
                    ? t('insuranceForm:insurance_inquiry.determine_eligible_text')
                    : t('insuranceForm:insurance_inquiry.pamm.determine_eligible_text')}
            </BodyTextMedium>
            <BodyTextMedium marginBottom={24}>
                {hpiEligibilityFlow !== HpiEligibilityFlow.Psychiatry
                    ? t('insuranceForm:insurance_inquiry.diagnosis_and_confidentiality_text')
                    : t('providers:pamm_provider.request.callout.description')}
            </BodyTextMedium>

            {!doesHaveOnlyOneInsuranceOption && (
                <SelectField
                    fieldId="selectedInsuranceId"
                    label={t('insuranceForm:insurance_inquiry.your_health_plan')}
                    options={selectOptions}
                    value={selectedInsuranceId}
                    onChange={option => option && setSelectedInsuranceId(option.value)}
                    containerStyle={selectStyles.container}
                    data-testid={CheckForInsurancePageTestID.Select}
                />
            )}
        </FlowLayout>
    );
};
